import { useState } from "react";
import { motion } from "framer-motion";
import { PawPrint, Heart, Headphones, Tv, Sparkles, Volume2, Play, Instagram } from "lucide-react";

const YT_ID = "VWO4VJj9TGs";
const IG_URL = "https://www.instagram.com/proactive_dental/";

const relaxMenu = [
  { icon: Heart, label: "Heated blankets" },
  { icon: Headphones, label: "Noise-cancelling headphones" },
  { icon: Tv, label: "Netflix & YouTube" },
  { icon: Volume2, label: "Stress balls" },
  { icon: PawPrint, label: "Cuddles with Winston" },
];

export default function Winston() {
  const [playing, setPlaying] = useState(false);

  return (
    <section id="winston" data-testid="winston-section" className="py-24 md:py-32 bg-pd-surface relative overflow-hidden">
      <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-pd-sage/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Media */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="space-y-5"
        >
          {/* Main YouTube tour */}
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-pd-forest/15 bg-pd-forest aspect-video">
              {playing ? (
                <iframe
                  data-testid="winston-youtube"
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${YT_ID}?autoplay=1&rel=0`}
                  title="Meet Winston — Proactive Dental Burpengary"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button
                  data-testid="winston-play-btn"
                  onClick={() => setPlaying(true)}
                  className="group relative w-full h-full"
                  aria-label="Play Winston video"
                >
                  <img
                    src={`https://i.ytimg.com/vi/${YT_ID}/maxresdefault.jpg`}
                    alt="Winston the therapy dog welcomes patients"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute inset-0 bg-pd-forest/30 group-hover:bg-pd-forest/20 transition-colors" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-20 h-20 rounded-full bg-pd-terra/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                      <Play size={30} className="text-white ml-1" fill="white" />
                    </span>
                  </span>
                  <span className="absolute bottom-4 left-4 bg-pd-bg/90 backdrop-blur text-pd-forest text-xs font-medium rounded-full px-3 py-1.5">
                    Watch the clinic tour with Winston · 2:18
                  </span>
                </button>
              )}
            </div>
            <div className="absolute -top-4 left-4 bg-pd-terra text-white rounded-full px-5 py-2 shadow-lg flex items-center gap-2 text-sm font-medium -rotate-3">
              <PawPrint size={16} /> Say hi to Winston!
            </div>
          </div>

          {/* Winston portrait photo */}
          <div className="flex items-stretch gap-5">
            <div className="relative rounded-[1.75rem] overflow-hidden shadow-xl bg-pd-forest w-44 sm:w-52 shrink-0 aspect-[3/4]">
              <video
                data-testid="winston-clip"
                src="/winston.mp4?v=5"
                poster="/winston-poster.jpg?v=5"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 flex flex-col justify-center bg-pd-bg rounded-[1.75rem] p-6 border border-pd-forest/5">
              <PawPrint size={24} className="text-pd-accent" />
              <p className="mt-3 font-display text-2xl text-pd-forest leading-snug">
                "I'm Winston — let reception know if you'd like me to say hi!"
              </p>
              <p className="mt-2 text-sm text-pd-muted">Ready to greet you at the door.</p>
            </div>
          </div>
        </motion.div>

        {/* Story */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-medium text-pd-sage">
            <PawPrint size={15} className="text-pd-accent" /> Our Therapy Dog
          </span>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl font-medium leading-tight text-pd-forest">
            Meet Winston, <br />
            <span className="italic text-pd-accent">a very good boy.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-pd-body">
            Fear and stress are the number one reasons people put off the dentist — so we did
            something about it. Winston is our trained therapy dog, here to help you feel calm,
            supported and in control from the moment you walk in.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-pd-body">
            Nervous about treatment? Just ask, and Winston will happily sit beside you during
            your appointment. Research shows a friendly dog can lower anxiety and blood
            pressure — and honestly, he's just lovely to have around.
          </p>

          <div className="mt-8">
            <div className="text-sm font-medium text-pd-forest mb-3 flex items-center gap-2">
              <Sparkles size={16} className="text-pd-accent" /> Plus our relaxation menu:
            </div>
            <div className="flex flex-wrap gap-2.5">
              {relaxMenu.map((m) => (
                <span
                  key={m.label}
                  data-testid={`relax-chip-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 bg-pd-bg border border-pd-forest/5 rounded-full px-4 py-2 text-sm text-pd-body shadow-sm"
                >
                  <m.icon size={15} className="text-pd-accent" /> {m.label}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="winston-book-btn"
              className="inline-flex items-center justify-center gap-2 bg-pd-forest hover:bg-pd-forest/90 text-white rounded-full px-8 py-4 font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-pd-forest/20"
            >
              <PawPrint size={18} /> Book a visit with Winston
            </a>
            <a
              href={IG_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="winston-instagram-btn"
              className="inline-flex items-center justify-center gap-2 border border-pd-forest/30 text-pd-forest hover:bg-pd-forest hover:text-white rounded-full px-8 py-4 font-medium transition-all duration-300"
            >
              <Instagram size={18} /> Follow Winston on Instagram
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
